/**
 * Exact decimal arithmetic for money and quantities without a dependency (ADR-0010: never use floats
 * for amounts). Values are BigInts scaled by 10^12; every rounding is ROUND_HALF_UP (away from zero on
 * a tie), the same as decimal.js on the backend, and happens exactly once per operation.
 */

export type Dec = bigint;

const SCALE = 12;
const ONE = 10n ** BigInt(SCALE);
export const ZERO: Dec = 0n;

const PATTERN = /^\s*([+-])?(\d*)(?:\.(\d*))?\s*$/;

function abs(n: bigint): bigint {
  return n < 0n ? -n : n;
}

/** n / d rounded half-up (away from zero on a tie). */
function divRound(n: bigint, d: bigint): bigint {
  if (d === 0n) throw new RangeError('Division by zero');
  const negative = n < 0n !== d < 0n;
  const an = abs(n);
  const ad = abs(d);
  let q = an / ad;
  if (2n * (an % ad) >= ad) q += 1n;
  return negative ? -q : q;
}

/** True for plain decimal strings such as "12", "-3.5", ".25". */
export function isDecimal(value: string | null | undefined): boolean {
  if (value === null || value === undefined) return false;
  const m = PATTERN.exec(value);
  return !!m && ((m[2] ?? '') !== '' || (m[3] ?? '') !== '');
}

/** Parses a decimal string or number; empty or invalid input is zero. */
export function dec(value: string | number | bigint | null | undefined): Dec {
  if (value === null || value === undefined) return ZERO;
  if (typeof value === 'bigint') return value * ONE;
  const text =
    typeof value === 'number' ? (Number.isFinite(value) ? value.toFixed(SCALE) : '') : value;
  const m = PATTERN.exec(text);
  if (!m) return ZERO;
  const int = m[2] ?? '';
  const frac = m[3] ?? '';
  if (int === '' && frac === '') return ZERO;
  let scaled = BigInt(int || '0') * ONE;
  if (frac.length <= SCALE) scaled += BigInt((frac || '0').padEnd(SCALE, '0'));
  else {
    const kept = BigInt(frac.slice(0, SCALE));
    const roundUp = Number(frac.charAt(SCALE)) >= 5;
    scaled += kept + (roundUp ? 1n : 0n);
  }
  return m[1] === '-' ? -scaled : scaled;
}

function unit(decimals: number): bigint {
  return 10n ** BigInt(SCALE - decimals);
}

/** Rounds to `decimals` places, half-up. */
export function round(value: Dec, decimals: number): Dec {
  const u = unit(decimals);
  return divRound(value, u) * u;
}

/** Truncates toward zero at `decimals` places. */
export function roundDown(value: Dec, decimals: number): Dec {
  const u = unit(decimals);
  return (value / u) * u;
}

/** round(a × b / c) at `decimals` places in one exact step (no double rounding). */
export function mulDivRound(a: Dec, b: Dec, c: Dec, decimals: number): Dec {
  return divRound(a * b, c * unit(decimals)) * unit(decimals);
}

/** round(a × b) at `decimals` places. */
export function mulRound(a: Dec, b: Dec, decimals: number): Dec {
  return mulDivRound(a, b, ONE, decimals);
}

/** a × b at full internal precision. */
export function mul(a: Dec, b: Dec): Dec {
  return divRound(a * b, ONE);
}

export function sum(values: Iterable<Dec>): Dec {
  let total = ZERO;
  for (const v of values) total += v;
  return total;
}

export function min(a: Dec, b: Dec): Dec {
  return a < b ? a : b;
}

export function max(a: Dec, b: Dec): Dec {
  return a > b ? a : b;
}

/** Fixed-scale string, e.g. toFixed(dec('1.005'), 2) === '1.01'. */
export function toFixed(value: Dec, decimals: number): string {
  const r = round(value, decimals) / unit(decimals);
  const negative = r < 0n;
  const digits = abs(r)
    .toString()
    .padStart(decimals + 1, '0');
  const int = digits.slice(0, digits.length - decimals);
  const frac = decimals > 0 ? `.${digits.slice(digits.length - decimals)}` : '';
  return `${negative ? '-' : ''}${int}${frac}`;
}

/** Shortest exact string (trailing zeros dropped), e.g. "2.5". */
export function toPlain(value: Dec): string {
  const s = toFixed(value, SCALE);
  return s.includes('.') ? s.replace(/\.?0+$/, '') : s;
}

/** For display only (Intl formatting). */
export function toNumber(value: Dec): number {
  return Number(toFixed(value, SCALE));
}

/** Number of fraction digits written in a decimal string. */
export function decimalPlaces(value: string): number {
  const m = PATTERN.exec(value);
  return m ? (m[3] ?? '').length : 0;
}

/**
 * Splits `total` across `weights` in proportion so the parts add up exactly (largest remainder; ties go
 * to the earliest index) — the backend's `prorate`, used for document-level discounts.
 */
export function prorate(total: Dec, weights: Dec[], decimals: number): Dec[] {
  const amount = round(total, decimals);
  const weightSum = sum(weights);
  if (weights.some((w) => w < 0n)) throw new RangeError('Weights must not be negative');
  if (weightSum === 0n) {
    if (amount === 0n) return weights.map(() => ZERO);
    throw new RangeError('Cannot prorate a non-zero amount over zero weights');
  }
  const u = unit(decimals);
  const denominator = weightSum * u;
  const parts = weights.map((w, i) => {
    const numerator = amount * w;
    return { i, floor: (numerator / denominator) * u, remainder: numerator % denominator };
  });
  const result = parts.map((p) => p.floor);
  let left = amount - sum(result);
  const order = [...parts].sort((a, b) =>
    a.remainder === b.remainder ? a.i - b.i : a.remainder > b.remainder ? -1 : 1,
  );
  for (const { i } of order) {
    if (left < u) break;
    result[i] = (result[i] ?? ZERO) + u;
    left -= u;
  }
  return result;
}
